// Minimal casting WebSocket client — speaks the same envelope as casting's
// frontend ws.js ({ token, meta, room, message }) against wss://casting.heyjoe.io.
// Used by the interview host page to drive the guest's phone app
// (start/stop-local-recording), and later the gimbal panel. The talent-* rooms
// we use are auth-free on the server, so the token is best-effort.

const WS_URL = (import.meta as any).env?.VITE_CASTING_WS_URL || 'wss://casting.heyjoe.io/sock';

let ws: WebSocket | null = null;
let rooms: string[] = [];
let pingItv: ReturnType<typeof setInterval> | undefined;
let reconnectTm: ReturnType<typeof setTimeout> | undefined;
let queue: string[] = [];
let lastRecv = 0; // liveness — any inbound frame counts
type CastingMessageHandler = (msg: Record<string, any>) => void;
let handlers: CastingMessageHandler[] = [];

/** Subscribe to relayed room messages ({type:'message', message}) on any joined room. Returns unsubscribe. */
export function onCastingMessage(h: CastingMessageHandler) {
  handlers.push(h);
  return () => { handlers = handlers.filter(x => x !== h); };
}

const token = () => localStorage.getItem('authToken') || localStorage.getItem('token') || '';

function connect() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  try { ws = new WebSocket(WS_URL); } catch { scheduleReconnect(); return; }
  ws.onopen = () => {
    lastRecv = Date.now();
    rooms.forEach(room => ws?.send(JSON.stringify({ token: token(), meta: 'join', room, talent: false })));
    queue.splice(0).forEach(f => ws?.send(f));
    clearInterval(pingItv);
    pingItv = setInterval(() => {
      if (ws?.readyState !== WebSocket.OPEN) return;
      // Half-dead sockets (NAT timeout, laptop sleep) stay readyState OPEN but
      // deliver nothing — if we've heard NOTHING for 35s despite 10s pings,
      // force-close so onclose reconnects and rejoins every room.
      if (Date.now() - lastRecv > 35000) { try { ws.close(); } catch { /* noop */ } return; }
      rooms.forEach(room => ws?.send(JSON.stringify({ token: token(), meta: 'ping', room })));
    }, 10000);
  };
  ws.onmessage = (ev) => {
    lastRecv = Date.now();
    try {
      const frame = JSON.parse(String(ev.data));
      if (frame && frame.type === 'message' && frame.message) handlers.forEach(h => { try { h(frame.message); } catch { /* noop */ } });
    } catch { /* non-JSON frame */ }
  };
  ws.onclose = () => { clearInterval(pingItv); scheduleReconnect(); };
  ws.onerror = () => { try { ws?.close(); } catch { /* noop */ } };
}

function scheduleReconnect() {
  clearTimeout(reconnectTm);
  reconnectTm = setTimeout(() => { if (rooms.length) connect(); }, 2500);
}

// Sleep/wake and network flaps kill sockets without firing onclose promptly —
// kick a reconnect check when the tab becomes visible or the network returns.
if (typeof window !== 'undefined') {
  const kick = () => {
    if (!rooms.length) return;
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) connect();
    else if (Date.now() - lastRecv > 35000) { try { ws.close(); } catch { /* noop */ } }
  };
  window.addEventListener('online', kick);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') kick(); });
}

export function joinCastingRoom(room: string) {
  if (!rooms.includes(room)) rooms.push(room);
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ token: token(), meta: 'join', room, talent: false }));
  else connect();
}

export function sendCastingRoomMessage(room: string, message: Record<string, unknown>) {
  const frame = JSON.stringify({ token: token(), meta: 'message', room, message });
  if (ws?.readyState === WebSocket.OPEN) ws.send(frame);
  else { queue.push(frame); connect(); }
}

export function leaveCastingWs() {
  rooms = []; queue = [];
  clearInterval(pingItv); clearTimeout(reconnectTm);
  try { ws?.close(); } catch { /* noop */ }
  ws = null;
}
